import React, { useCallback } from 'react';
import moment from 'moment';
import { Card, Divider, } from '@sede-enterprise/shell-ds-react-framework';
import {
    formatDate,
    mmOrInch,
    mmOrMiles,
    round,
    getSiteType,
    getLastDate,
    getLastValue,
    findFirstNonZeroCorrosionRate,
    getExpressions
} from '../../util/helperFunctions';
import { helperTexts } from '../../util/helperTexts';
import { useEvalMetrics } from '../../util/hooks/useEvalMetrics';
import InfoLabelWithTooltip from '../../components/InfoPanel/InfoLabelWithToolTip';
import InfoData from '../../components/InfoPanel/InfoData';
import * as R from "rambda";

const panel_end = moment();
const helpContents = helperTexts.clInfo;

const CLInfoPanel = ({ units, corrosionLoopID, site, location }) => {
    const { isRhienland } = getSiteType(site);
    const MeasuredWallThicknessMetric = isRhienland
        ? 'RHE_MeasuredWallThickness_UI'
        : 'MeasuredWallThickness';
    const PredictedWallThicknessMetric = isRhienland
        ? 'LiveModelPredictedWallThickness_RHE'
        : 'LiveModelWallThickness';

    const findLastDate = (inputdata, element) => getLastDate(inputdata, corrosionLoopID, element);
    const findLastValue = (inputdata, element) => getLastValue(inputdata, corrosionLoopID, element);

    const expressions = useCallback(() => getExpressions(isRhienland), [isRhienland]);

    const eval_data = useEvalMetrics('CLExpression', corrosionLoopID, null, expressions);

    const renderLocationDetails = () => (
        <div className='info-details'>
            <h4>Location</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <InfoData
                        label='Site'
                        value={R.path(['siteName'], location)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Prod Unit'
                        value={R.path(['plant'], location)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Unit'
                        value={R.path(['unit'], location)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Equipment'
                        value={R.path(['equipment'], location)}
                        unitFormatter={null}
                    />
                    <InfoData
                        label='Corrosion Loop'
                        value={corrosionLoopID}
                        unitFormatter={null}
                    />
                </div>
            </div>
        </div>
    );

    const renderMeasurements = () => (
        <div className='info-details'>
            <h4>Measurements</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <InfoData
                        label='Last measured average wall thickness'
                        value={
                            units
                                ? round(0.0393700787 * findLastValue(eval_data, MeasuredWallThicknessMetric), 3)
                                : round(findLastValue(eval_data, MeasuredWallThicknessMetric), 3)
                        }
                        units={units}
                        date={
                            findLastDate(eval_data, MeasuredWallThicknessMetric)
                        }
                        unitFormatter={mmOrInch}
                    />
                </div>
            </div>
        </div>
    );

    const renderPredictions = () => (
        <div className='info-details'>
            <h4>Prediction</h4>
            <div className='info-details-body'>
                <div className='data-col'>
                    <div className='info-data'>
                        <InfoLabelWithTooltip
                            label='Current average wall thickness'
                            tooltipContent={helpContents.CurrentAvgWT}
                        />
                        <p className='data-value'>
                            {units
                                ? round(0.0393700787 * findLastValue(eval_data, PredictedWallThicknessMetric), 3)
                                : round(findLastValue(eval_data, PredictedWallThicknessMetric), 3)
                            }
                            {mmOrInch(units)}{' '}
                            <span className='titleLabelDate'>{formatDate(new Date(panel_end))}</span>
                        </p>
                    </div>
                    <div className='info-data'>
                        <InfoLabelWithTooltip
                            label='Current average corrosion rate'
                            tooltipContent={helpContents.CurrentAvgCr}
                        />
                        <p className='data-value'>
                            {units
                                ? round(39.3700787402 * findFirstNonZeroCorrosionRate(
                                    R.path(
                                        ['result', corrosionLoopID, 'LiveModelCorrosionRate', 'data'],
                                        eval_data
                                    )
                                ), 3) : findFirstNonZeroCorrosionRate(
                                    R.path(
                                        ['result', corrosionLoopID, 'LiveModelCorrosionRate', 'data'],
                                        eval_data
                                    )
                                )}{' '}
                            {mmOrMiles(units)}{' '}
                            <span className='titleLabelDate'>{formatDate(new Date(panel_end))}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className='content-body'>
            <Card className='details-card' bodyPadding={false}>
                <div className='details-card-body'>
                    {renderLocationDetails()}
                    <div className='details-container'>
                        <Divider direction='vertical' className='vertical-divider' />
                        {renderMeasurements()}
                    </div>
                    <div className='details-container'>
                        <Divider direction='vertical' className='vertical-divider' />
                        {renderPredictions()}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default CLInfoPanel;